import { Fragment, useState } from "react";
import {
  Autocomplete,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import BTDialogBox from "../../../components/BTDialgBox";
import BTInput from "../../../components/BTInput";
import BTButton from "../../../components/BTButton";
import BTBox from "../../../components/BTBox";
import BTTypography from "../../../components/BTTypography";
import EnhancedTable from "../../../components/BTTable";
import secureLocalStorage from "react-secure-storage";
import authService from "../../../services/auth-service";
import { useNotification } from "../../../context";
import { Notifications, Restore, Edit, Delete } from "@mui/icons-material";
import { isEqual } from "lodash";
import { useDispatch } from "react-redux";
import { get, insert, update, remove } from "../../../store/labSlice";
const Addprice = ({ open, setOpen, services, parties, priceList, sizes }) => {
  const { addNotification } = useNotification();
  const { u_id, admin_u_id, u_type } = secureLocalStorage.getItem("userdata");
  const dispatch = useDispatch();
  const initialState = {
    p_id: null,
    ser_id: null,
    size_id: null,
    ser_price_type: null,
    ser_rate: null,
    pl_desc: null,
    pl_created_by: u_id,
    pl_updated_by: u_id,
    admin_u_id: u_type === 1 ? u_id : admin_u_id,
  };

  const [formData, setFormData] = useState(open);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingRowId, setEditingRowId] = useState([]);

  // Create a deep frozen copy of the initial data for comparison
  const frozenInitialData = JSON.parse(JSON.stringify(open));
  Object.freeze(frozenInitialData);

  const validateForm = (records) => {
    const errors = {};
    records?.items?.forEach((record, index) => {
      const recordErrors = {};
      if (!record.ser_id) recordErrors.ser_id = "Service is required";
      if (!record.ser_rate || record.ser_rate <= 0) {
        recordErrors.ser_rate = "Rate must be greater than 0";
      }
      if (record.ser_price_type === 2 && !record.size_id) {
        recordErrors.size_id = "Size selection is required";
      }
      if (Object.keys(recordErrors).length > 0) {
        errors[index] = recordErrors;
      }
    });
    return errors;
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(null);
  };

  const submitPricelist = async (e) => {
    e.preventDefault();
    const priceErrors = validateForm(formData);
    setErrors(priceErrors);

    if (Object.keys(priceErrors).length === 0) {
      if (isEqual(formData?.items, frozenInitialData?.items)) {
        return addNotification({
          icon: <Notifications color="white" />,
          title: "Error!",
          message: "No changes to save",
          color: "error",
        });
      }

      try {
        setLoading(true);
        const response = await authService.update_pricelist({
          attributes: { data: formData?.items },
        });
        if (response?.status === 1) {
          dispatch(
            update({
              name: "priceList",
              key: "pl_id",
              data: response?.data,
              fetch: true,
            })
          );
          addNotification({
            icon: <Notifications color="white" />,
            title: "Success!",
            message: response?.message,
            color: "success",
          });
          handleClose();
        }
      } catch (error) {
        addNotification({
          icon: <Notifications color="white" />,
          title: "Error!",
          message: "Something went wrong",
          color: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (index, fieldName, value) => {
    setFormData((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [fieldName]: value,
      };

      if (fieldName === "ser_id") {
        const selectedService = services.find((s) => s.ser_id === value);
        if (selectedService) {
          updatedItems[index].ser_price_type = selectedService.ser_price_type;
          updatedItems[index].ser_rate = selectedService.ser_rate || "";
        }
      }

      return { ...prev, items: updatedItems };
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[index]?.[fieldName]) {
        delete newErrors[index][fieldName];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      return newErrors;
    });
  };

  const addFields = () => {
    setFormData((prev) => ({
      ...prev,
      items: [{ ...initialState, p_id: prev.p_id }, ...prev.items],
    }));
  };

  const removeFields = (index) => {
    if (index?.pl_id) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.pl_id === index.pl_id
            ? { ...item, pl_deleted: item.pl_deleted ? null : 1 }
            : item
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index),
      }));
    }
  };

  const setDefaultPrice = () => {
    const defaultPriceList = priceList
      ?.filter(({ p_id }) => p_id === 0)
      ?.map((item) => ({
        ...item,
        pl_id: null,
        p_id: formData.p_id,
        pl_created_by: u_id,
        pl_updated_by: u_id,
        admin_u_id: u_type === 1 ? u_id : admin_u_id,
      }));
    setFormData((prev) => ({
      ...prev,
      items: [
        ...defaultPriceList,
        ...prev?.items?.map((item) => ({ ...item, pl_deleted: 1 })),
      ],
    }));
  };

  const handleEditClick = (row, index) => {
    const rowId = row?.pl_id || index;
    setEditingRowId((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const columns = [
    {
      id: "id",
      name: "ID",
      selector: (row, index) => index + 1,
      width: "0.1rem",
    },
    {
      id: "service",
      name: "Service",
      selector: (row, index) => {
        const isEditing =
          editingRowId.includes(row?.pl_id || index) || !row?.pl_id;
        const servicesData = services?.map(({ ser_id, ser_name }) => ({
          value: ser_id,
          label: ser_name,
        }));

        if (isEditing) {
          return (
            <Autocomplete
              options={servicesData || []}
              getOptionLabel={(option) => option.label}
              value={servicesData?.find(
                (option) => option.value === row.ser_id
              )}
              onChange={(_, value) =>
                handleChange(index, "ser_id", value?.value)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  sx={{
                    position: "relative",
                    top: "2px",
                  }}
                  fullWidth
                  error={!!errors[index]?.ser_id}
                  helperText={errors[index]?.ser_id}
                />
              )}
            />
          );
        }
        return services?.find(({ ser_id }) => ser_id === row.ser_id)?.ser_name;
      },
      bodyCellStyle: {
        padding: "0px",
      },
      width: "100px",
    },
    {
      id: "size_id",
      name: "Size",
      selector: (row, index) => {
        if (row.ser_price_type === 1) return "Fixed";

        const isEditing =
          editingRowId.includes(row?.pl_id || index) || !row?.pl_id;
        const size = sizes?.find(({ size_id }) => size_id === row.size_id);

        if (isEditing) {
          return (
            <Autocomplete
              options={sizes || []}
              getOptionLabel={(option) =>
                `${option.size_from?.toFixed(2) || 0} - ${
                  option.size_to?.toFixed(2) || 0
                }`
              }
              value={size || null}
              onChange={(_, value) =>
                handleChange(index, "size_id", value?.size_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  sx={{
                    position: "relative",
                    top: "2px",
                  }}
                  v
                  fullWidth
                  error={!!errors[index]?.size_id}
                  helperText={errors[index]?.size_id}
                />
              )}
            />
          );
        }
        return size
          ? `${size.size_from.toFixed(2)} - ${size.size_to.toFixed(2)}`
          : "";
      },
      bodyCellStyle: {
        padding: "0px",
      },
      width: "100px",
    },
    {
      id: "ser_rate",
      name: "Service Rate",
      selector: (row, index) => {
        const isEditing =
          editingRowId.includes(row?.pl_id || index) || !row?.pl_id;
        if (isEditing) {
          return (
            <BTInput
              name="ser_rate"
              type="number"
              value={row.ser_rate || ""}
              onChange={(e) => handleChange(index, "ser_rate", e.target.value)}
              variant="standard"
              fullWidth
              sx={{
                position: "relative",
                top: "2px",
              }}
              error={!!errors[index]?.ser_rate}
              helperText={errors[index]?.ser_rate}
            />
          );
        }
        return row.ser_rate;
      },
      bodyCellStyle: {
        padding: "0px",
      },
      width: "100px",
    },
    {
      id: "pl_desc",
      name: "Description",
      selector: (row, index) => {
        const isEditing =
          editingRowId.includes(row?.pl_id || index) || !row?.pl_id;
        if (isEditing) {
          return (
            <BTInput
              name="pl_desc"
              type="text"
              value={row.pl_desc || ""}
              onChange={(e) => handleChange(index, "pl_desc", e.target.value)}
              variant="standard"
              fullWidth
              sx={{
                position: "relative",
                top: "2px",
              }}
            />
          );
        }
        return row.pl_desc;
      },
      bodyCellStyle: {
        padding: "0px",
      },
      width: "100px",
    },
    {
      id: "actions",
      name: "Actions",
      selector: (row, index) => (
        <BTBox>
          <IconButton onClick={() => handleEditClick(row, index)} size="small">
            <Edit fontSize="12px" color="success" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => removeFields(row?.pl_id ? row : index)}
          >
            {row?.pl_deleted === 1 ? (
              <Restore color="success" fontSize="12px" />
            ) : (
              <Delete fontSize="12px" color="error" />
            )}
          </IconButton>
        </BTBox>
      ),
      width: "100px",
      bodyCellStyle: {
        padding: "0px",
      },
      center: true,
    },
  ];

  return (
    <BTDialogBox
      open={Boolean(open)}
      fullWidth
      maxWidth="lg"
      minHeight="500px"
      onSubmit={submitPricelist}
      close={handleClose}
      title="Edit price"
      buttons={[
        {
          name: "Cancel",
          color: "secondary",
          size: "small",
          onClick: handleClose,
        },
        {
          name: "Save",
          variant: "gradient",
          type: "submit",
          color: "dark",
          size: "small",
          loading: loading,
          loadingComponent: (
            <Fragment>
              <svg width={0} height={0}>
                <defs>
                  <linearGradient
                    id="my_gradient"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#e01cd5" />
                    <stop offset="100%" stopColor="#1CB5E0" />
                  </linearGradient>
                </defs>
              </svg>
              <CircularProgress
                size={22}
                sx={{ "svg circle": { stroke: "url(#my_gradient)" } }}
              />
            </Fragment>
          ),
          sx: { ml: 2, width: "80px" },
        },
      ]}
    >
      <BTBox
        display="flex"
        alignItems="center"
        gap={2}
        bgColor={"white"}
        position={"fixed"}
      >
        <BTTypography variant="h6" fontWeight={600}>
          {parties?.find(({ p_id }) => p_id === formData.p_id)?.p_name}
        </BTTypography>
        <BTButton
          variant="gradient"
          onClick={addFields}
          color="dark"
          size="small"
        >
          Add Item
        </BTButton>
        <BTButton
          variant="gradient"
          onClick={setDefaultPrice}
          color="dark"
          disabled={
            formData?.p_id === 0 ||
            formData?.items?.some((pl) => pl?.pl_id === null)
          }
          size="small"
        >
          Set Default
        </BTButton>
      </BTBox>
      <BTBox mt={6}>
        <EnhancedTable
          rows={formData.items}
          columns={columns}
          rowsPerPageOptions={[10, 20, 50]}
          defaultRowsPerPage={10}
          title="Price List"
          onDeleteSelected={null}
          tableHeader={false}
          pagination={true}
        />
      </BTBox>
    </BTDialogBox>
  );
};

export default Addprice;
